import { AMPLITUDE_EVENT } from '@sonar-software/sonar-analytics';
import { getInitiativeRoute } from '@lib/routes';
import { getInitiativeTableColumns } from './InitiativesTableColumns';
import { Initiative } from '@sonar-software/sonar-core';
import { InitiativesTableActionBar } from './InitiativesTableActionBar';
import isEqual from 'lodash/isEqual';
import { SortByFilterQueryParams } from '@periscopes/general/filters';
import { SorterResult } from 'antd/es/table/interface';
import { TableProps } from 'antd';
import { useNavigate } from 'react-router-dom';

import { COPY_SPECIFIC, INITIATIVES_TABLE_KEYS } from '@sonar-software/sonar-ts-constants';
import { KeyPagination, keyPaginationFallbackQueryParams } from '@ui/KeyPagination';
import { KeyPaginationQueryParams, PaginationDirection, SonarSortDirection } from '@sonar-software/sonar-ts-types';
import React, { useCallback, useMemo, useRef } from 'react';
import { SelectionTable, SelectionTableQueryParams } from '@ui/tables';
import { useAmplitude, useCurrentUser, useInitiatives, useQueryParams, useSelectionTableWithInversion } from '@hooks';

export type InitiativesTableQueryParams = KeyPaginationQueryParams &
	SortByFilterQueryParams &
	SelectionTableQueryParams & {
		assignedTos?: number[];
		statuses?: string[];
		title?: string;
	};

const columns = getInitiativeTableColumns();
const statusColumn = columns.find((x) => x.key === INITIATIVES_TABLE_KEYS.STATUS);
const assignedToColumn = columns.find((x) => x.key === INITIATIVES_TABLE_KEYS.ASSIGNED_TO);

const getRowKey = (initiative: Initiative) => initiative.id;
export interface InitiativesTableProps {
	id?: string;
}

export const InitiativesTable: React.FC<InitiativesTableProps> = ({ id }) => {
	const navigate = useNavigate();
	const logEvent = useAmplitude();
	const { isReadOnly } = useCurrentUser();
	const { data, request, isRequested } = useInitiatives(true);
	const { results, totalCount } = data;

	const scrollableListRef = useRef<HTMLDivElement>(null);

	const selectionData = useSelectionTableWithInversion({
		getRowKey,
		results,
		totalCount
	});

	const { queryParams, updateParams } = useQueryParams<InitiativesTableQueryParams>({
		defaultParams: useInitiatives.defaultRequest,
		fallbackParams: keyPaginationFallbackQueryParams,
		onUpdate: (newParams, prev, combined) => {
			const didSearchChange = combined.title !== prev?.title;
			const didStatusChange = !isEqual(combined.statuses, prev?.statuses);
			const didAssignedTosChange = !isEqual(combined.assignedTos, prev?.assignedTos);

			const didTableFiltersChange = didSearchChange || didStatusChange || didAssignedTosChange;

			request(combined);

			if (didTableFiltersChange) {
				selectionData?.resetSelection();
			}
		}
	});

	const calculatedColumns = useMemo(
		() => [
			...columns.slice(0, 1),
			{
				...statusColumn,
				filteredValue: queryParams.statuses
			},
			{
				...assignedToColumn,
				filteredValue: queryParams.assignedTos?.map((userId) => String(userId))
			},
			...columns.slice(3)
		],
		[ queryParams.assignedTos, queryParams.statuses ]
	);

	const handleRowClick = useCallback(
		(record: Initiative) => {
			logEvent({ id: AMPLITUDE_EVENT.INITIATIVE_DETAILS_VIEWED });
			navigate(getInitiativeRoute(record.id));
		},
		[ logEvent, navigate ]
	);

	const handleOnTableChange = useCallback<NonNullable<TableProps<Initiative>['onChange']>>(
		(pagination, filters, sorter: SorterResult<Initiative>) => {
			const sortDirection = `${sorter.order || 'descend'}ing` as SonarSortDirection;
			const didSortDirectionChange = sortDirection !== queryParams.sortDirection;

			updateParams({
				...queryParams,
				assignedTos: filters[ INITIATIVES_TABLE_KEYS.ASSIGNED_TO ]?.map((userId) => Number(userId)) ?? [],
				sortBy: sorter.columnKey as string,
				sortDirection,
				statuses: (filters[ INITIATIVES_TABLE_KEYS.STATUS ] as string[]) ?? [],
				...didSortDirectionChange ?
					{
						key: '',
						paginationDirection: PaginationDirection.Forward
					  } :
					{}
			});
		},
		[ queryParams, updateParams ]
	);

	return (
		<>
			<SelectionTable
				actionBar={
					<InitiativesTableActionBar
						onUpdate={ updateParams }
						queryParams={ queryParams }
						selectionData={ selectionData } />
				}
				bordered
				columns={ calculatedColumns }
				dataSource={ results }
				forceShowSelection={ !isReadOnly }
				id={ id }
				loading={ isRequested }
				onChange={ handleOnTableChange }
				onRowClick={ handleRowClick }
				ref={ scrollableListRef }
				rowKey={ getRowKey }
				selectionData={ selectionData }
				selectionType={ COPY_SPECIFIC.INITIATIVES } />
			<KeyPagination
				autoHide
				defaultPageSize={ Number(queryParams.count) }
				listRef={ scrollableListRef }
				onChange={ updateParams }
				resource={ data } />
		</>
	);
};
